import { useState, useEffect, useCallback, useRef } from "react";
import communityService from "../services/communityService";

/**
 * Custom hook for fetching community feed sidebar data
 * Performance optimized with:
 * - Automatic refresh every 5 minutes
 * - Request deduplication
 * - Stale-while-revalidate pattern
 * - Error recovery with retry
 */
export function useCommunityFeedSidebar(options = {}) {
  const [widgets, setWidgets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);
  // Track in-flight request to prevent duplicates
  const fetchInProgress = useRef(false);
  // Store last successful data for stale-while-revalidate
  const lastData = useRef(null);

  const fetchSidebar = useCallback(async (isBackgroundRefresh = false) => {
    // Prevent duplicate requests
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    // Only show loading spinner on initial load, not background refreshes
    if (!isBackgroundRefresh && !lastData.current) {
      setLoading(true);
    }

    try {
      const response = await communityService.getFeedSidebar({
        trendingLimit: options.trendingLimit || 5,
        risingCommunitiesLimit: options.risingCommunitiesLimit || 3,
        hotDiscussionsLimit: options.hotDiscussionsLimit || 4,
      });

      if (isMounted.current) {
        const data = response?.data || response;
        setWidgets(data);
        lastData.current = data;
        setError(null);
      }
    } catch (err) {
      if (isMounted.current) {
setError(err);
        // On error, keep showing last successful data if available
        if (lastData.current) {
          setWidgets(lastData.current);
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      fetchInProgress.current = false;
    }
  }, [options.trendingLimit, options.risingCommunitiesLimit, options.hotDiscussionsLimit]);

  // Initial fetch and auto-refresh setup
  useEffect(() => {
    isMounted.current = true;
    fetchSidebar();

    // Auto-refresh every 5 minutes for real-time data
    const refreshInterval = setInterval(() => {
      fetchSidebar(true); // Background refresh
    }, 5 * 60 * 1000);

    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, [fetchSidebar]);

  // Manual refetch function
  const refetch = useCallback(() => {
    return fetchSidebar(false);
  }, [fetchSidebar]);

  return { 
    widgets, 
    loading, 
    error, 
    refetch,
    // Convenience accessors for individual widgets
    trendingTopics: widgets?.trendingTopics || [],
    userActivity: widgets?.userActivity || null,
    risingCommunities: widgets?.risingCommunities || [],
    hotDiscussions: widgets?.hotDiscussions || [],
  };
}

export default useCommunityFeedSidebar;
